/**
 * Add-on resolution — AeriCast, Video Wall, OptiSound, AI, OptiDev, Engage, Branding.
 *
 * Add-ons are NOT plan documents; they live inside `productPlans[*].add_ons` and staff-api
 * flattens them into one list. They get their OWN line kind rather than sharing the
 * subscription sku space, because `engage` is both a plan alias and an add-on key — one
 * namespace could not tell an Engage plan from the Engage add-on.
 *
 * Three shapes here are easy to get wrong, and each one silently mis-prices:
 *
 *   1. TIERS. OptiSound sells Standard and Pro. The tier does not change the term, it
 *      changes which PRICE KEY the term maps to: Standard reads `yearly`, Pro reads
 *      `pro_yearly`. Resolving the term alone always prices Standard ($162) even when the
 *      customer asked for Pro ($270).
 *   2. minQuantity. OptiDev is 3, which the portal sells as a "$324 base package (applies
 *      to up to 3 screens)" — 3 x $108 is the floor, so a quote for 1 screen still bills 3.
 *   3. limitCurrencies. OptiSound is usd/cad only. Quoting it in EUR is not a missing
 *      price to fall back from; it is not for sale, and must be refused rather than
 *      quietly billed at the USD rate.
 */
import { toCents } from './cents';
import { normalizeTerm } from './plans';
import type { CatalogAddOn, CatalogAddOnTier, PlanTerm } from './types';
import type { MatchResult } from './catalog';

/** A one-time add-on is a flat charge; quantity does not multiply it. */
export function isOneTime(doc: CatalogAddOn): boolean {
  return (doc.type ?? '').toLowerCase() === 'onetime';
}

function collapse(s: string | undefined | null): string {
  return (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Exact match on the add-on `key` or its display name, collapsed — so
 * `brandingCustomDomain`, `branding-custom-domain` and `Branding` all resolve. No fuzzy
 * matching, for the same reason as plans and products: a wrong add-on is a wrong price.
 */
export function matchAddOn(
  query: string | undefined,
  key: string | undefined,
  addOns: CatalogAddOn[],
): MatchResult<CatalogAddOn> {
  for (const ident of [key, query]) {
    if (!ident) continue;
    const ic = collapse(ident);
    for (const a of addOns) {
      if (ic && (ic === collapse(a.key) || ic === collapse(a.name))) {
        return { match: a, reason: 'key' };
      }
    }
  }
  const ident = key || query;
  return {
    match: null,
    reason: ident ? `'${ident}' is not an add-on in the catalog` : 'nothing selected',
  };
}

/**
 * The tier to price at: the operator's choice, else the catalog's `defaultTier`, else the
 * first tier. `null` when the add-on has no tiers at all.
 */
export function resolveTier(
  doc: CatalogAddOn,
  requested?: string,
): { tier: CatalogAddOnTier | null; unknownRequest: boolean } {
  const tiers = doc.tiers ?? [];
  if (!tiers.length) return { tier: null, unknownRequest: false };

  if (requested) {
    const want = collapse(requested);
    const hit = tiers.find((t) => collapse(t.key) === want || collapse(t.name) === want);
    if (hit) return { tier: hit, unknownRequest: false };
    // Asked for a tier we don't have — fall back to the default, but say so. Silently
    // pricing Standard when someone typed "Pro" is the failure this guards.
    return {
      tier: tiers.find((t) => collapse(t.key) === collapse(doc.defaultTier)) ?? tiers[0],
      unknownRequest: true,
    };
  }
  return {
    tier: tiers.find((t) => collapse(t.key) === collapse(doc.defaultTier)) ?? tiers[0],
    unknownRequest: false,
  };
}

/**
 * Which price key a tier reads for a term. Standard's keys are the bare terms
 * (`yearly`); Pro's are prefixed (`pro_yearly`), and `yearly_freedevice` becomes
 * `pro_yearly_freedevice` — so match the exact term or a `_`-suffixed form rather than
 * assuming a prefix.
 */
export function priceKeyFor(tier: CatalogAddOnTier | null, term: PlanTerm): string {
  if (!tier) return term;
  const exact = tier.priceKeys.find((k) => k === term);
  if (exact) return exact;
  const suffixed = tier.priceKeys.find((k) => k.endsWith(`_${term}`));
  return suffixed ?? term;
}

export interface ResolvedAddOnPrice {
  cents: number | null;
  fallback: boolean;
  term: PlanTerm;
  /** The price key actually read — useful in a flag message. */
  priceKey: string;
  /** Set when the add-on isn't sold in the quote's currency. */
  unavailableIn?: string;
  /** True when the rate came from Stripe rather than Mongo. */
  fromStripe?: boolean;
}

/**
 * Per-screen (or flat, for a one-time add-on) price in cents, at the given tier.
 *
 * A currency outside `limitCurrencies` is refused outright — no USD fallback — because the
 * add-on genuinely isn't sold there and billing the dollar figure would invent a price.
 * A currency merely absent from an unrestricted add-on still falls back to USD, flagged,
 * matching how plans and hardware behave.
 *
 * For an `isAutoConvertCurrency` add-on the non-USD rate comes from STRIPE — see below.
 */
export function resolveAddOnPrice(
  doc: CatalogAddOn,
  currency: string,
  term?: string,
  requestedTier?: string,
): ResolvedAddOnPrice {
  const cur = (currency || 'usd').toLowerCase();
  const normalized = normalizeTerm(term);
  const { tier } = resolveTier(doc, requestedTier);
  const priceKey = priceKeyFor(tier, normalized);

  const limits = (doc.limitCurrencies ?? []).map((c) => c.toLowerCase());
  if (limits.length && !limits.includes(cur)) {
    return {
      cents: null,
      fallback: false,
      term: normalized,
      priceKey,
      unavailableIn: cur,
    };
  }

  // ── Stripe first, for the add-ons whose non-USD rates only exist there ──
  // Four add-ons are flagged `isAutoConvertCurrency` and hold ONLY a usd price in Mongo.
  // The portal reads their other currencies from Stripe `currency_options`, so reading
  // Mongo here would print the dollar figure behind a euro sign — up to +34% on GBP.
  //
  // Already CENTS. It must never go through `toCents`, which is why it arrives on its own
  // field rather than merged into `prices`.
  if (doc.isAutoConvertCurrency && cur !== 'usd') {
    const amounts = doc.stripeRates?.[priceKey]?.[cur];
    if (typeof amounts?.perScreenCents === 'number') {
      return { cents: amounts.perScreenCents, fallback: false, term: normalized, priceKey, fromStripe: true };
    }
    // No Stripe rate: either the key could not read it, or Stripe holds no per-currency
    // amount for this price. OptiDev is the standing example — it is a tiered price whose
    // non-USD tiers are simply not configured, and the portal falls back the same way we
    // are about to. Fall through to Mongo, which will flag the USD fallback.
  }

  const byCurrency = doc.prices || {};
  let node = byCurrency[cur];
  let fallback = false;
  if (!node) {
    node = byCurrency.usd;
    fallback = true;
  }

  const entry = node?.[priceKey];
  if (!entry || entry.price === undefined || entry.price === null) {
    return { cents: null, fallback, term: normalized, priceKey };
  }
  return { cents: toCents(entry.price), fallback, term: normalized, priceKey };
}

/**
 * Units actually billed. A one-time add-on bills once; everything else bills at least
 * `minQuantity`, which is how the portal presents OptiDev's 3-screen base package.
 */
export function billableUnits(doc: CatalogAddOn, qty: number): number {
  if (isOneTime(doc)) return 1;
  return Math.max(qty, doc.minQuantity ?? 1);
}
